// tippi/src/pages/carrier/VehicleForm.js
import React, { useState } from 'react';
import { carrierApi } from '../../firebase/functionsService';
import {
  EMPTY_VEHICLE_FORM, formFromVehicle, payloadFromForm, validateVehicleForm,
} from '../../utils/vehicleForm';
import { BODY_TYPE_LABEL, FUEL_LABEL } from '../../utils/vehicleStatus';
import Field from './Field';
import { ui } from './carrierStyles';

// Bilkort: opprett (vehicle = null) eller rediger. Serveren validerer på nytt og er fasit.
//   drivers: [{ id, name }] aktive sjåfører i firmaet, til valget av standardsjåfør
export default function VehicleForm({ vehicle = null, orgId, drivers = [], onSaved, onCancel }) {
  const [form, setForm] = useState(() => (vehicle ? formFromVehicle(vehicle) : EMPTY_VEHICLE_FORM));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    const found = validateVehicleForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStatus('saving');
    setMessage('');
    try {
      const res = await carrierApi.saveOrgVehicle({ orgId, vehicleId: vehicle?.id ?? null, input: payloadFromForm(form) });
      setStatus('idle');
      if (onSaved) onSaved(res?.vehicleId ?? vehicle?.id);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Kunne ikke lagre bilen');
    }
  }

  const away = form.status === 'out_of_service';

  return (
    <form onSubmit={submit} noValidate>
      <Field id="v-plate" label="Skiltnummer" error={errors.registrationPlate}>
        <input id="v-plate" style={ui.input} value={form.registrationPlate} onChange={set('registrationPlate')} maxLength={12} autoCapitalize="characters" />
      </Field>
      <Field id="v-make" label="Merke" error={errors.make}>
        <input id="v-make" style={ui.input} value={form.make} onChange={set('make')} maxLength={40} />
      </Field>
      <div style={ui.row}>
        <Field id="v-body" label="Biltype" style={{ flex: 1, minWidth: 140 }}>
          <select id="v-body" style={ui.input} value={form.bodyType} onChange={set('bodyType')}>
            {Object.entries(BODY_TYPE_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field id="v-fuel" label="Drivstoff" style={{ flex: 1, minWidth: 140 }}>
          <select id="v-fuel" style={ui.input} value={form.fuelType} onChange={set('fuelType')}>
            {Object.entries(FUEL_LABEL).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field id="v-axles" label="Aksler" error={errors.axles} style={{ flex: '0 0 100px' }}>
          <input id="v-axles" style={ui.input} value={form.axles} onChange={set('axles')} inputMode="numeric" />
        </Field>
      </div>
      <div style={ui.row}>
        <Field id="v-curb" label="Egenvekt (kg)" error={errors.curbWeightKg} style={{ flex: 1, minWidth: 140 }}>
          <input id="v-curb" style={ui.input} value={form.curbWeightKg} onChange={set('curbWeightKg')} inputMode="numeric" />
        </Field>
        <Field id="v-payload" label="Lovlig nyttelast (kg)" error={errors.legalPayloadKg} style={{ flex: 1, minWidth: 140 }}>
          <input id="v-payload" style={ui.input} value={form.legalPayloadKg} onChange={set('legalPayloadKg')} inputMode="numeric" />
        </Field>
        <Field id="v-gross" label="Lovlig bruttovekt (kg)" error={errors.legalGrossWeightKg} style={{ flex: 1, minWidth: 140 }}>
          <input id="v-gross" style={ui.input} value={form.legalGrossWeightKg} onChange={set('legalGrossWeightKg')} inputMode="numeric" />
        </Field>
      </div>
      <Field id="v-inspection" label="Neste EU-kontroll" error={errors.nextInspectionDate}>
        <input id="v-inspection" type="date" style={ui.input} value={form.nextInspectionDate} onChange={set('nextInspectionDate')} />
      </Field>
      <Field id="v-driver" label="Standardsjåfør (valgfri)">
        <select id="v-driver" style={ui.input} value={form.defaultDriverUid} onChange={set('defaultDriverUid')}>
          <option value="">Ingen fast sjåfør</option>
          {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      </Field>
      <Field id="v-status" label="Status">
        <select id="v-status" style={ui.input} value={form.status} onChange={set('status')}>
          <option value="active">I drift</option>
          <option value="out_of_service">Ute av drift</option>
        </select>
      </Field>
      {away && (
        <>
          <Field id="v-reason" label="Årsak" error={errors.outOfServiceReason}>
            <input id="v-reason" style={ui.input} value={form.outOfServiceReason} onChange={set('outOfServiceReason')} maxLength={200} />
          </Field>
          <div style={ui.row}>
            <Field id="v-from" label="Ute fra" style={{ flex: 1, minWidth: 140 }}>
              <input id="v-from" type="date" style={ui.input} value={form.outOfServiceFrom} onChange={set('outOfServiceFrom')} />
            </Field>
            <Field id="v-until" label="Ute til (valgfri)" style={{ flex: 1, minWidth: 140 }}>
              <input id="v-until" type="date" style={ui.input} value={form.outOfServiceUntil} onChange={set('outOfServiceUntil')} />
            </Field>
          </div>
        </>
      )}

      {status === 'error' && <p role="alert" style={ui.error}>{message}</p>}
      <div style={{ ...ui.row, marginTop: 16 }}>
        <button type="submit" className="tippi-btn" style={ui.btn} disabled={status === 'saving'}>
          {status === 'saving' ? 'Lagrer…' : 'Lagre bil'}
        </button>
        {onCancel && <button type="button" style={ui.btnSecondary} onClick={onCancel}>Avbryt</button>}
      </div>
    </form>
  );
}
